import { useDispatch, useSelector } from 'react-redux';

// Design System
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectSelectedPointProps } from './ClickAndCollectSelectedPoint.types';

// Config
import clickAndCollectSelectedPointState from './ClickAndCollectSelectedPoint.state';
import routes from '@constants/routeConstants';
import {
  openMoreDetailsModal,
  changeClickAndCollectSelectedPoint,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import isCollectionPointAvailable from '@utils/collection/isCollectionPointAvailable';
import { getSelectedStoreUnavailableMessage } from '@utils/collection/getSelectedStoreUnavailableMessage';

// Components
import ClickAndCollectCountdown from '@components/click-and-collect-countdown';
import ClickAndCollectCollectionPoint from '@components/click-and-collect-collection-point';
import CollectorDetails from '@components/collector-details/CollectorDetails';
import Container from '@components/container';
import Heading from '@components/heading';

// Styles
import styles from './click-and-collect-selected-point.scss';

const ClickAndCollectSelectedPoint = ({
  autoFocusMoreDetailsButton = false,
  collectionPoint,
  disabled = false,
}: ClickAndCollectSelectedPointProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    collectFromTime,
    countdownVisible,
    cutOffTimeForSelectedStore,
    deliveryOptions,
    isSelectCollectionPointCallActive,
    savedCollectionPoints = [],
    collectPlusAvailable,
    showSecureIcon,
  } = useSelector(clickAndCollectSelectedPointState);

  const onMoreDetailsClick = () =>
    dispatch(openMoreDetailsModal({ id: collectionPoint?.id, route: routes.CLICK_AND_COLLECT }));
  const onChangeShopClick = () => dispatch(changeClickAndCollectSelectedPoint());

  const isAvailable = isCollectionPointAvailable(collectionPoint, deliveryOptions);

  const storeUnavailableMessage = getSelectedStoreUnavailableMessage(collectionPoint, collectPlusAvailable);
  const countdownContainerProps = countdownVisible
    ? { borderBottom: 'grey-15', marginBottom: '3', paddingBottom: '2' }
    : { marginBottom: '2' };

  return (
    <Container width="full">
      {storeUnavailableMessage && (
        <Container marginBottom="2">
          <Message
            body={storeUnavailableMessage?.body}
            dataTestId={storeUnavailableMessage?.dataTestId}
            title={storeUnavailableMessage?.title}
            type="error"
          />
        </Container>
      )}

      <Container {...countdownContainerProps}>
        <Heading label="Selected shop" tag="h4" type="s" />
      </Container>

      <ClickAndCollectCountdown
        className="mb-4"
        collectFromTime={collectFromTime}
        cutOffTime={cutOffTimeForSelectedStore}
      />

      <ClickAndCollectCollectionPoint
        as="div"
        autoFocus={autoFocusMoreDetailsButton}
        collectionPoint={collectionPoint}
        disabled={disabled}
        hideBorder={countdownVisible}
        isCallActive={isSelectCollectionPointCallActive}
        isSelected
        onChangeShopClick={onChangeShopClick}
        onMoreDetailsClick={onMoreDetailsClick}
        savedCollectionPoints={savedCollectionPoints}
      />

      {isAvailable ? (
        <CollectorDetails disabled={disabled || isSelectCollectionPointCallActive} showSecureIcon={showSecureIcon} />
      ) : (
        <PrimaryButton
          className={styles.changeShopButton}
          data-testid="click-collect-change-button"
          disabled={disabled || isSelectCollectionPointCallActive}
          onClick={onChangeShopClick}
        >
          Change shop
        </PrimaryButton>
      )}
    </Container>
  );
};

export default ClickAndCollectSelectedPoint;
